import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  CircularProgress,
  Box,
  Fade,
  Skeleton,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AuditServices from '../../../../services/audit.services';

const UserStatsTable = ({ startDate, endDate }) => {
  const [userStats, setUserStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shadowLoading, setShadowLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalUsers, setTotalUsers] = useState(0);
  // Track if this is the first load to show full loading or shadow loading
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    const fetchUserStats = async () => {
      // Use full loading indicator on first load, shadow loading on subsequent loads
      if (isFirstLoad) {
        setLoading(true);
      } else {
        setShadowLoading(true);
      }
      
      try {
        // Use the getUserStats endpoint which supports pagination
        const response = await AuditServices.getUserStats(
          page + 1, // API uses 1-based indexing
          rowsPerPage,
          startDate,
          endDate
        );
        
        // Extract usernames from email addresses
        const processedData = response.users.map(user => {
          const username = extractUsernameFromEmail(user.username);
          return { ...user, displayName: username };
        });
        
        setUserStats(processedData);
        setTotalUsers(response.totalUsers);
        setIsFirstLoad(false);
      } catch (error) {
        console.error('Error fetching user statistics:', error);
      } finally {
        setLoading(false);
        setShadowLoading(false);
      }
    };
    
    fetchUserStats();
  }, [startDate, endDate, page, rowsPerPage, isFirstLoad]);

  // Extract username from email (e.g., "john.doe@example.com" -> "John Doe")
  const extractUsernameFromEmail = (email) => {
    if (!email) return 'Unknown User';
    
    // Remove the domain part
    const localPart = email.split('@')[0];
    
    // Handle different email formats
    if (localPart.includes('.')) {
      // john.doe@example.com -> John Doe
      return localPart
        .split('.')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    } else {
      // johndoe@example.com -> Johndoe
      return localPart.charAt(0).toUpperCase() + localPart.slice(1);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Get unique module names from all user statistics
  const getUniqueModules = () => {
    const modules = new Set();
    userStats.forEach(user => {
      Object.keys(user.moduleStats || {}).forEach(module => {
        modules.add(module);
      });
    });
    return Array.from(modules).sort();
  };
  
  const uniqueModules = getUniqueModules();
  
  // Generate skeleton rows for loading state
  const renderSkeletonRows = () => {
    return Array.from(new Array(rowsPerPage)).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell>
          <Skeleton animation="wave" height={24} />
        </TableCell>
        {uniqueModules.map((_, moduleIndex) => (
          <TableCell key={`skeleton-cell-${moduleIndex}`} align="center">
            <Skeleton animation="wave" height={24} />
          </TableCell>
        ))}
        <TableCell align="center">
          <Skeleton animation="wave" height={24} />
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Paper elevation={2} sx={{ mt: 4, mb: 4, overflow: 'hidden', borderRadius: 2 }}>
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          User Leaderboard per Date Range
        </Typography>
        <Tooltip title="Scroll through data or use pagination below. Default is 15 rows per page, but can be increased up to 100." arrow placement="left">
          <IconButton size="small">
            <InfoIcon fontSize="small" sx={{ color: '#1D9999' }} />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Box position="relative">
        {/* Full page loading spinner */}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 5
          }}>
            <CircularProgress />
          </Box>
        )}
        
        <TableContainer sx={{ maxHeight: 600, position: 'relative' }}>
          {/* Shadow loading overlay */}
          <Fade in={shadowLoading}>
            <Box sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
              zIndex: 3,
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '100px'
            }}>
              <CircularProgress />
            </Box>
          </Fade>
          
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    position: 'sticky', 
                    left: 0, 
                    backgroundColor: '#f5f5f5',
                    zIndex: 3 
                  }}
                >
                  User
                </TableCell>
                
                {uniqueModules.map((module) => (
                  <TableCell key={module} align="center">
                    {module}
                  </TableCell>
                ))}
                
                <TableCell 
                  align="center"
                >
                  Total Activity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Conditionally render real data or skeleton rows based on loading state */}
              {loading && isFirstLoad ? renderSkeletonRows() : (
                userStats.map((user, index) => (
                  <TableRow 
                    key={user.username} 
                    hover
                    sx={{ 
                      '&:nth-of-type(odd)': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.02)' 
                      } 
                    }}
                  >
                    <TableCell 
                      component="th" 
                      scope="row"
                      sx={{ 
                        position: 'sticky', 
                        left: 0, 
                        backgroundColor: index % 2 ? '#fff' : 'rgba(0, 0, 0, 0.02)', 
                        zIndex: 1 
                      }}
                    >
                      {user.displayName}
                    </TableCell>
                    
                    {uniqueModules.map((module) => (
                      <TableCell 
                        key={`${user.username}-${module}`} 
                        align="center"
                      >
                        {user.moduleStats[module] || 0}
                      </TableCell>
                    ))}
                    
                    <TableCell 
                      align="center"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {user.totalCount}
                    </TableCell>
                  </TableRow>
                ))
              )}
              
              {!loading && userStats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={uniqueModules.length + 2} align="center" sx={{ py: 3 }}>
                    No data found matching the current filters
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[15, 30, 50, 100]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid #e0e0e0',
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#fff',
            zIndex: 2
          }}
        />
      </Box>
    </Paper>
  );
};

export default UserStatsTable;